import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import {
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../config/api';

/**
 * Auswertungs-Tab im Admin.
 *
 * Der Bericht (Texte, Kennzahlen, Grafiken als Inline-SVG) wird komplett im Backend erzeugt
 * (backend/auswertung_bericht.py) und hier nur angezeigt. So sehen App, PDF und die offline
 * erzeugte Datei garantiert gleich aus und rechnen mit denselben Zahlen.
 */
const OSPAuswertung = () => {
  const [bericht, setBericht] = useState('');
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState('');
  const [hoehe, setHoehe] = useState(1200);
  const rahmen = useRef(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      const antwort = await api.get('/api/admin/analytics/bericht', { responseType: 'text' });
      setBericht(antwort.data);
    } catch (e) {
      console.error('Fehler beim Abrufen der Auswertung:', e);
      setFehler('Die Auswertung konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    laden();
  }, [laden]);

  // iframe so hoch wie sein Inhalt, damit nur die Seite scrollt und nicht der Rahmen
  const hoeheAnpassen = useCallback(() => {
    // am <main> messen: documentElement.scrollHeight ist nie kleiner als der Rahmen selbst und würde nur wachsen
    const inhalt = rahmen.current?.contentDocument?.querySelector('main');
    if (inhalt) {
      setHoehe(Math.ceil(inhalt.getBoundingClientRect().height) + 8);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', hoeheAnpassen);
    return () => window.removeEventListener('resize', hoeheAnpassen);
  }, [hoeheAnpassen]);

  // Druckt nur den Bericht (A4-Layout aus dem Druck-CSS); im Druckdialog "Als PDF sichern" wählen
  const alsPdf = () => {
    const fenster = rahmen.current?.contentWindow;
    if (fenster) {
      fenster.focus();
      fenster.print();
    }
  };

  const herunterladen = () => {
    const url = URL.createObjectURL(new Blob([bericht], { type: 'text/html;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `OSP_Auswertung_${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">Auswertung</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={laden} disabled={laedt}>
            Aktualisieren
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={herunterladen} disabled={!bericht}>
            HTML
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={alsPdf} disabled={!bericht}>
            PDF / Drucken
          </Button>
        </Box>
      </Box>

      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}

      {laedt && !bericht ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        bericht && (
          <iframe
            ref={rahmen}
            title="OSP-Auswertung"
            srcDoc={bericht}
            onLoad={hoeheAnpassen}
            style={{ width: '100%', height: hoehe, border: 0, display: 'block', borderRadius: 8 }}
          />
        )
      )}
    </>
  );
};

export default OSPAuswertung;
